import { useEffect, useRef, useState } from 'react';

export interface PullToRefreshState {
  distance: number;
  refreshing: boolean;
}

/**
 * 下拉刷新只处理主页面中“单指、纵向、页面在顶部”的拖动：
 * - 抽屉/弹层内滚动用 data-no-pull-refresh 直接绕过；
 * - 横向拖动优先留给联赛栏，不调用 preventDefault；
 * - 只有明显向下拖动才进入刷新手势。
 */
export function usePullToRefresh(onRefresh: () => Promise<void> | void, options: { enabled?: boolean; threshold?: number } = {}): PullToRefreshState {
  const { enabled = true, threshold = 72 } = options;
  const [distance, setDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef<number | null>(null);
  const startX = useRef<number | null>(null);
  const active = useRef(false);
  const distanceRef = useRef(0);
  const refreshRef = useRef(onRefresh);
  refreshRef.current = onRefresh;

  useEffect(() => {
    if (!enabled) return;
    const ignoredTarget = (target: EventTarget | null) => target instanceof Element && Boolean(target.closest('[data-no-pull-refresh]'));
    const atTop = () => window.scrollY <= 0 && (document.scrollingElement?.scrollTop ?? 0) <= 0;
    const reset = () => {
      startY.current = null;
      startX.current = null;
      active.current = false;
      distanceRef.current = 0;
      setDistance(0);
    };
    const onTouchStart = (event: TouchEvent) => {
      if (event.touches.length !== 1 || ignoredTarget(event.target) || refreshing || !atTop()) return;
      startY.current = event.touches[0]!.clientY;
      startX.current = event.touches[0]!.clientX;
      active.current = false;
    };
    const onTouchMove = (event: TouchEvent) => {
      if (startY.current === null || startX.current === null || ignoredTarget(event.target)) return;
      if (!atTop()) { reset(); return; }
      const deltaY = event.touches[0]!.clientY - startY.current;
      const deltaX = event.touches[0]!.clientX - startX.current;
      if (deltaY <= 0 || Math.abs(deltaX) > Math.abs(deltaY)) {
        if (active.current) reset();
        return;
      }
      if (deltaY < 8) return;
      active.current = true;
      if (event.cancelable) event.preventDefault();
      const next = Math.min(96, deltaY * 0.5);
      distanceRef.current = next;
      setDistance(next);
    };
    const finish = () => {
      if (startY.current === null) return;
      const pulled = distanceRef.current;
      reset();
      if (pulled >= threshold) {
        setRefreshing(true);
        void Promise.resolve(refreshRef.current()).finally(() => setRefreshing(false));
      }
    };
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', finish, { passive: true });
    window.addEventListener('touchcancel', finish, { passive: true });
    return () => {
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', finish);
      window.removeEventListener('touchcancel', finish);
    };
  }, [enabled, refreshing, threshold]);

  return { distance, refreshing };
}